import * as d3 from 'd3';
import { DateTime } from 'luxon';
import { margin } from './defaults';
export function lineRenderer(svgElement, chartData, opts) {
    const bounds = svgElement.getBoundingClientRect();
    const width = bounds.width - margin.left - margin.right;
    const height = bounds.height - margin.top - margin.bottom;
    // X axis
    const x = d3.scaleUtc().range([0, width]).domain(chartData.xDomain);
    // Y axis
    const y = d3.scaleLinear().range([height, 0]).domain(chartData.yDomain).nice();
    // --------------------------------------------------------------------------
    // Main chart elements
    // Main container
    const svg = d3
        .select(svgElement)
        .attr('min-width', '200px')
        .attr('min-height', '200px');
    // Remove existing content
    svg.selectAll('*').remove();
    // Main SVG g element, translated to accommodate margins
    const svgGroup = svg
        .append('g')
        .attr('transform', 'translate(' + margin.left + ',' + margin.top + ')');
    // --------------------------------------------------------------------------
    // Axes
    // Add x-axis to SVG
    svgGroup
        .append('g')
        .attr('class', 'xAxis')
        .attr('transform', 'translate(0,' + height + ')')
        .call(d3.axisBottom(x).ticks(30).tickSize(15).tickFormat(opts.xTickFormatter));
    // Add y-axis to SVG
    svgGroup
        .append('g')
        .attr('class', 'yAxis')
        .call(d3
        .axisLeft(y)
        .ticks(4)
        .tickSize(-width)
        .tickPadding(10)
        .tickFormat(opts.yTickFormatter));
    // Make 1st and last tick larger on x axis to set text position and hide them
    svgGroup
        .selectAll('.xAxis line')
        .attr('y2', (_d, i, selection) => i === 0 || i === selection.length - 1 ? 15 : 0)
        .attr('opacity', 0);
    // Only show every 1st and last number on the x axis
    // and position them inside the boundaries of the graph
    svgGroup
        .selectAll('.xAxis text')
        .attr('opacity', (_d, i, selection) => i === 0 || i === selection.length - 1 ? 1 : 0)
        .attr('text-anchor', (_d, i, selection) => i === 0 ? 'start' : i === selection.length - 1 ? 'end' : 'middle');
    // --------------------------------------------------------------------------
    // Main chart drawing
    // Datasets
    const datasetSelection = svgGroup
        .selectAll('.dataset')
        .data(chartData.dataSets)
        .enter();
    // Add the dataset lines
    datasetSelection
        .append('path')
        .datum((dataset) => dataset.rows)
        .attr('stroke', (data, i) => data[i].dataSet.color)
        .attr('stroke-width', 3)
        .attr('fill', 'transparent')
        .attr('class', (_dataset, i) => 'area' + i)
        .attr('d', d3
        .line()
        .x((d) => x(d.day) ?? 0)
        .y((d) => y(d.value) ?? 0));
    // Add data point dots for mouseover
    const dots = datasetSelection
        .append('circle')
        .datum((dataset) => dataset.rows)
        .attr('class', (_data, i) => `circle-${i}`)
        .attr('cx', (data, i) => x(data[i].day) ?? 0)
        .attr('cy', (data, i) => y(data[i].value) ?? 0)
        .attr('r', 4)
        .attr('fill', (data, i) => data[i].dataSet.color)
        .attr('opacity', 0);
    // --------------------------------------------------------------------------
    // Interactivity
    // Add the vertical ruler line to the SVG.
    // This will get made visible and repositioned by mousemove events.
    const ruler = svgGroup
        .append('line')
        .attr('class', 'ruler')
        .attr('shape-rendering', 'crispEdges')
        .attr('y1', y(chartData.yDomain[0]) ?? 0)
        .attr('y2', y(chartData.yDomain[1]) ?? 0)
        .attr('opacity', 0);
    // Show tooltip and ruler when user moves mouse around the chart
    svg.on('mousemove', () => {
        // Find x-axis time nearest to mouse cursor.
        const [mouseX] = d3.mouse(svgElement);
        // This value will not be a precise day, so it will need to be rounded
        const time = DateTime.fromJSDate(x.invert(mouseX - margin.left), {
            zone: 'UTC',
        });
        // Round up or down, but stay within limits of domain
        const rounded = time.hour < 12
            ? time.startOf('day')
            : time.plus({ hours: 12 }).startOf('day');
        const maxDate = DateTime.max(rounded, DateTime.fromJSDate(chartData.xDomain[0]));
        const finalDate = DateTime.min(maxDate, DateTime.fromJSDate(chartData.xDomain[1]));
        const day = finalDate.toJSDate();
        // Get data for day
        const rows = chartData.allRows.filter((row) => row.day.getTime() === day.getTime());
        // Show ruler
        ruler
            .style('opacity', 0.9)
            .attr('x1', x(day) ?? 0)
            .attr('x2', x(day) ?? 0);
        // Show data points
        dots
            .data(rows)
            .style('opacity', 1)
            .attr('cx', (row) => x(row.day) ?? 0)
            .attr('cy', (row) => y(row.value) ?? 0);
        // Fire tooltip event
        if (opts.onHover) {
            opts.onHover(rows);
        }
    });
    // Hide ruler when mouse leaves the container
    svg.on('mouseleave', () => {
        ruler.style('opacity', 0);
        dots.style('opacity', 0);
        if (opts.onHover) {
            opts.onHover(undefined);
        }
    });
}
